import { c as _c } from "react/compiler-runtime";
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Block, Document, Inline } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import type { ReactNode } from 'react';
import React from 'react';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Image } from '@sola/ark-skippy/atoms/image/image.tsx';
import { Video } from '@sola/ark-skippy/atoms/video/video.tsx';
import { Card } from '@sola/ark-skippy/molecules/card/card.tsx';
import { BasicContentMain } from '@sola/ark-skippy/organisms-cms/basic-content/main/basic-content-main.tsx';
import { GalleryCinema } from '@sola/ark-skippy/organisms-cms/gallery/cinema/gallery-cinema.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import { getArticlePath } from '@sola/contentful-model/lib/article.ts';
import type { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { ContentResponseBody, ContentResponseDto } from '@sola/schema/lib/contentful.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { decapitalize, slugify } from '@sola/utils/string.ts';
import { slashes } from '@sola/utils/url.ts';
import { isAnchorLink, isMailToLink, isTelLink } from '@sola/utils/url.ts';
import { convertContentResponseToFlexibleContent, convertMediaResponseToMedia } from './response.tsx';
const handleArrayChildren = (children: React.ReactNode, keyPrefix: string) => {
  return React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        key: child.key || `${keyPrefix}-child-${index}`
      });
    }
    return child;
  });
};
const Bold = t0 => {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = handleArrayChildren(children, "bold");
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="bold">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const Italic = t0 => {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = handleArrayChildren(children, "italic");
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="italic">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const SoftReturn = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <br />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const getRichText = (body: ContentResponseBody | null | undefined, articleType?: string) => {
  if (!body) {
    return;
  }
  const embedCounter = {
    embed: 0,
    media: 0,
    update: 0,
    heading: 0
  };

  // Helper to extract plain text from a rich text node
  const getTextFromNode = (node: Block | Inline): string => {
    if (!node.content) {
      return '';
    }
    return node.content.map(child => {
      if ('value' in child && typeof child.value === 'string') {
        return child.value;
      }
      if ('content' in child) {
        return getTextFromNode(child as Block | Inline);
      }
      return '';
    }).join('');
  };
  const tableOfContents: Array<{
    id: string;
    title: string;
    subtitle?: string;
  }> = [];
  const options = {
    preserveWhitespace: true,
    renderText: (text: React.ReactNode) => {
      const str = text?.toString() ?? '';
      const lines = str.split('\n');
      return <>
          {lines.map((line, index) => <React.Fragment key={`line-${index}-${line}`}>
              {line}
              {index < lines.length - 1 && <SoftReturn key={index} />}
            </React.Fragment>)}
        </>;
    },
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: (text: React.ReactNode) => <Italic>{text}</Italic>
    },
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node: Block | Inline, children: React.ReactNode) => {
        const {
          data
        } = node;
        const targetId = (data as {
          target?: {
            sys: {
              id: string;
            };
          };
        })?.target?.sys?.id;
        const link = body?.links?.entries?.hyperlink?.find?.(item => item?.sys?.id === targetId);
        const isDownload = decapitalize(link?.__typename) === keys.contentTypes.downloads;
        let path = link?.path;
        if (!path && link?.slug && link?.taxonomyArticleType) {
          path = slashes(getArticlePath(link.slug, link.taxonomyArticleType as ArticleTypesEnum), 'both');
        }
        if (isDownload) {
          path = `/file/${targetId}`;
        }
        return <Link target={isDownload ? '_blank' : undefined} href={path || '#'}>
            {children}
          </Link>;
      },
      [INLINES.HYPERLINK]: (node: Block | Inline, children: React.ReactNode) => {
        const {
          data
        } = node;
        const uri = (data as {
          uri?: string;
        })?.uri || '';
        const isAnchor = isAnchorLink(uri);
        const isMailto = isMailToLink(uri);
        const isTel = isTelLink(uri);

        // Anchor, mailto, and tel links aren't routable — use plain anchors.
        if (isAnchor) {
          return <a href={uri}>{children}</a>;
        }
        if (isMailto) {
          const mailtoUri = uri.includes('mailto:') ? uri : `mailto:${uri}`;
          return <a href={mailtoUri}>{children}</a>;
        }
        if (isTel) {
          const telUri = uri.includes('tel:') ? uri : `tel:${uri}`;
          return <a href={telUri}>{children}</a>;
        }

        // The Link atom handles all routing decisions:
        // - Skippy routes → client-side navigation (NextLink)
        // - Legacy same-domain → plain <a>, _self (full nav for www-router)
        // - Truly external → plain <a>, _blank
        return <Link href={uri}>{children}</Link>;
      },
      [BLOCKS.HEADING_2]: (node: Block | Inline, children: React.ReactNode) => {
        embedCounter.heading += 1;
        const textContent = getTextFromNode(node);
        const id = slugify(textContent) || `heading-${embedCounter.heading}`;
        if (textContent) {
          tableOfContents.push({
            id,
            title: textContent
          });
          return <h2 id={id} className="heading-lg">
              {children}
            </h2>;
        }
        return null;
      },
      [BLOCKS.HEADING_3]: (node: Block | Inline, children: React.ReactNode) => {
        return <h3 className="heading-md">{children}</h3>;
      },
      [BLOCKS.HEADING_4]: (node: Block | Inline, children: React.ReactNode) => {
        return <h4 className="heading-sm">{children}</h4>;
      },
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: React.ReactNode) => {
        return <p>{children}</p>;
      },
      [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: React.ReactNode) => {
        return <li>{children}</li>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: Block | Inline) => {
        const {
          data
        } = node;
        const target = (data as {
          target?: {
            sys: {
              id: string;
            };
          };
        })?.target;
        const link = Array.isArray(body?.links?.entries?.block) ? body?.links?.entries?.block.find((item: {
          sys: {
            id?: string;
          };
        }) => item?.sys?.id === target?.sys.id) : undefined;
        if (!link || !link.__typename) {
          return <>Content not found</>;
        }
        if (link.__typename === 'ImageWrapper') {
          embedCounter.media += 1;
          const convertedMedia = convertMediaResponseToMedia(link);
          const id = `media-${embedCounter.media}`;
          return <div className="Richtext__embed" id={id}>
              {!!convertedMedia && <>
                  {convertedMedia.video?.url && <Video {...convertedMedia.video} url={convertedMedia.video.url} />}
                  {convertedMedia?.image && !convertedMedia.video?.url && <Image image={convertedMedia?.image} aspectRatio="16-9" rounded="lg" />}
                  {convertedMedia.caption && <div className="Richtext__embed-attribution label label-lg mt-comfy-sm">
                      {convertedMedia.caption}
                    </div>}
                </>}
            </div>;
        }
        if (link.__typename === 'FlexibleContent') {
          let renderBody;
          if (link.body) {
            renderBody = link.body.json ? getRichText(link.body)?.content : documentToReactComponents(link.body as unknown as Document);
          }
          const convertedContent = convertContentResponseToFlexibleContent(link);
          if (link.items && link.items.length || link.itemsCollection?.items && link.itemsCollection.items.length) {
            embedCounter.embed += 1;
            const id = `embed-${embedCounter.embed}`;
            tableOfContents.push({
              id,
              title: convertedContent?.title || `Gallery ${embedCounter.embed}`
            });
            const items: FlexibleContentProps[] = link.items ? link.items : link.itemsCollection.items?.map((item: ContentResponseDto) => {
              let renderBodyItem;
              if (item.body) {
                renderBodyItem = item.body.json ? getRichText(item.body)?.content : documentToReactComponents(item.body as unknown as Document);
              }
              const convertedItem = convertContentResponseToFlexibleContent(item);
              return {
                ...convertedItem,
                body: renderBodyItem,
                media: convertedItem?.media
              };
            }) || [];
            return <div className="Richtext__embed" id={id}>
                <GalleryCinema {...convertedContent} media={undefined} body={renderBody as ReactNode} items={items} alt inline />
              </div>;
          }
          if (articleType === keys.articleTypes.crisisUpdate) {
            embedCounter.update += 1;
            const id = `update-${embedCounter.update}`;
            tableOfContents.push({
              id,
              title: convertedContent?.title || `Update ${embedCounter.update}`,
              subtitle: convertedContent?.displayDate
            });
            return <div className="Richtext__embed" id={id}>
                <Card {...convertedContent} body={renderBody as ReactNode} variant="article" />
              </div>;
          }
          embedCounter.embed += 1;
          const id = `section-${embedCounter.embed}`;
          tableOfContents.push({
            id,
            title: convertedContent?.title || `Section ${embedCounter.embed}`
          });
          return <div className="Richtext__embed" id={id}>
              <BasicContentMain {...convertedContent} body={renderBody as ReactNode} alt inline />
            </div>;
        }
        return <>Invalid embedded entry</>;
      }
    }
  };
  const content = documentToReactComponents(body?.json as unknown as Document, options) as ReactNode;
  return {
    content,
    tableOfContents
  };
};